import React, { useState } from 'react';
import { Form, Formik } from 'formik';
import { Button } from 'react-bootstrap';
import * as Yup from 'yup';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import homepageApi from '../../api/homepage';
import CustomInput from '../../common/FormComponents/Input';
import ImageInput from '../../common/FormComponents/ImageInput';
import CustomModal from '../../common/UI/CustomModal';
import DeleteConfirmationModal from '../../common/UI/CustomModal/deleteModal';
import { toastError, toastSuccess } from '../../common/UI/Toasts';
import { getFormDataFromObject } from '../../utils/helpers';
import ReactSwitch from 'react-switch';
import editIcon from '../../assets/images/pencil.svg';
import deleteIcon from '../../assets/images/trash.svg';
import Loader from '../../common/UI/Loader';
import ThemeEditor from './ThemeEditor';

const validationSchema = Yup.object({
  name: Yup.string().required('Name is required'),
  title: Yup.string().required('Title is required'),
  bio: Yup.string(),
  image: Yup.array().min(1, 'Image is required'),
});

const ExpertsManager = ({ section, backToList, refreshSections }) => {
  const [items, setItems] = useState(section.items || []);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showThemeEditor, setShowThemeEditor] = useState(false);

  const sortedItems = [...items].sort((a, b) => a.order - b.order);

  const handleAddNew = () => {
    setEditingItem(null);
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem({
      ...item,
      image: item.image ? [{ PrevUrl: item.image.location, ...item.image }] : [],
    });
    setShowModal(true);
  };

  const handleDelete = (itemId) => {
    setDeleteItemId(itemId);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    setLoading(true);
    try {
      await homepageApi.deleteItemFromSection(section.sectionKey, deleteItemId);
      toastSuccess('Expert deleted successfully');
      refreshSections();
      fetchItems();
    } catch (error) {
      toastError('Failed to delete expert');
      console.error(error);
    } finally {
      setLoading(false);
      setShowDeleteModal(false);
      setDeleteItemId(null);
    }
  };

  const fetchItems = async () => {
    try {
      const response = await homepageApi.getHomepageSection(section.sectionKey);
      setItems(response?.data?.data?.items || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleToggleStatus = async (itemId, currentStatus) => {
    setLoading(true);
    try {
      const payload = getFormDataFromObject({ status: !currentStatus });
      await homepageApi.updateItemInSection(section.sectionKey, itemId, payload);
      toastSuccess('Status updated successfully');
      fetchItems();
    } catch (error) {
      toastError('Failed to update status');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (values, { resetForm }) => {
    setLoading(true);
    try {
      let payload = {
        name: values.name,
        title: values.title,
        bio: values.bio,
        status: values.status !== undefined ? values.status : true,
        imageType: 'image',
      };

      // Handle image upload
      if (values.image[0]?.imgData && values.image[0]?.imgData instanceof File) {
        payload.file = values.image[0].imgData;
      } else if (editingItem && values.image[0]?.PrevUrl) {
        payload.image = editingItem.image;
      }

      payload = getFormDataFromObject(payload);

      if (editingItem) {
        await homepageApi.updateItemInSection(section.sectionKey, editingItem._id, payload);
        toastSuccess('Expert updated successfully');
      } else {
        await homepageApi.addItemToSection(section.sectionKey, payload);
        toastSuccess('Expert added successfully');
        resetForm();
      }

      setShowModal(false);
      setEditingItem(null);
      fetchItems();
      refreshSections();
    } catch (error) {
      toastError(editingItem ? 'Failed to update expert' : 'Failed to add expert');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleDragEnd = async (result) => {
    if (!result.destination) return;

    const reorderedItems = Array.from(sortedItems);
    const [removed] = reorderedItems.splice(result.source.index, 1);
    reorderedItems.splice(result.destination.index, 0, removed);

    // Update order
    const updatedItems = reorderedItems.map((item, index) => ({
      _id: item._id,
      order: index,
    }));

    setItems(reorderedItems.map((item, index) => ({ ...item, order: index })));

    try {
      await homepageApi.reorderItems(section.sectionKey, { items: updatedItems });
      toastSuccess('Order updated successfully');
    } catch (error) {
      toastError('Failed to update order');
      fetchItems(); // Revert on error
    }
  };

  if (loading) {
    return <Loader loading={loading} />;
  }

  return (
    <div className="content_wrapper all_products_main">
      <div className="container-fluid">
        {/* Breadcrumb */}
        <div className="row">
          <div className="col-md-12">
            <ol className="breadcrumb">
              <li onClick={backToList} style={{ cursor: 'pointer', color: '#007bff' }}>
                <i className="fa fa-arrow-left me-2"></i>
                Landing Page CMS
              </li>
              <li>Experts Management</li>
            </ol>
          </div>
        </div>

        {/* Main Content */}
        <div className="row">
          <div className="col-md-12">
            <div className="box_main">
              <div className="header_top border-bottom pb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h3 className="mb-1">
                      <i className="fa fa-user-md me-2 text-primary"></i>
                      Experts Management
                    </h3>
                    <p className="text-muted mb-0 small">
                      Manage your team of healthcare professionals and experts
                    </p>
                  </div>
                  <div className="d-flex gap-2 align-items-center">
                    <Button variant="outline-primary" onClick={() => setShowThemeEditor(true)}>
                      <i className="fa fa-palette me-2"></i>
                      Theme
                    </Button>
                    <Button variant="outline-secondary" onClick={backToList}>
                      <i className="fa fa-times me-2"></i>
                      Close
                    </Button>
                    <Button variant="primary" onClick={handleAddNew}>
                      <i className="fa fa-plus me-2"></i>
                      Add New Expert
                    </Button>
                  </div>
                </div>
              </div>

              <div className="middle_main pt-4">
                {sortedItems.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="fa fa-user-md fa-4x text-muted mb-3 d-block"></i>
                    <h5 className="text-muted mb-2">No Experts Added Yet</h5>
                    <p className="text-muted mb-4">Start by adding your first healthcare expert</p>
                    <Button variant="primary" onClick={handleAddNew}>
                      <i className="fa fa-plus me-2"></i>
                      Add First Expert
                    </Button>
                  </div>
                ) : (
                  <>
                    {/* Info Banner */}
                    <div className="alert alert-info d-flex align-items-center mb-4" role="alert">
                      <i className="fa fa-info-circle me-2"></i>
                      <small>Drag and drop cards to reorder. Total experts: <strong>{sortedItems.length}</strong></small>
                    </div>

                    {/* Drag & Drop List */}
                    <DragDropContext onDragEnd={handleDragEnd}>
                      <Droppable droppableId="experts">
                        {(provided, snapshot) => (
                          <div
                            {...provided.droppableProps}
                            ref={provided.innerRef}
                            className="row"
                            style={{
                              backgroundColor: snapshot.isDraggingOver ? '#f8f9fa' : 'transparent',
                              transition: 'background-color 0.2s ease',
                              borderRadius: '8px',
                              padding: '10px',
                            }}
                          >
                            {sortedItems.map((item, index) => (
                              <Draggable key={item._id} draggableId={item._id} index={index}>
                                {(provided, snapshot) => (
                                  <div
                                    className="col-md-6 col-lg-4 mb-4"
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                  >
                                    <div
                                      className="card h-100 border-0 shadow-sm"
                                      style={{
                                        transform: snapshot.isDragging ? 'rotate(3deg)' : 'none',
                                        boxShadow: snapshot.isDragging
                                          ? '0 8px 16px rgba(0,0,0,0.2)'
                                          : '0 2px 8px rgba(0,0,0,0.08)',
                                        transition: 'all 0.2s ease',
                                        cursor: 'grab',
                                      }}
                                    >
                                      {/* Drag Handle Indicator */}
                                      <div
                                        className="text-center py-2 bg-light border-bottom"
                                        style={{ cursor: 'grab' }}
                                      >
                                        <i className="fa fa-grip-horizontal text-muted"></i>
                                      </div>

                                      {/* Expert Image */}
                                      {item.image?.location && (
                                        <div style={{ position: 'relative', overflow: 'hidden' }}>
                                          <img
                                            src={item.image.location}
                                            alt={item.name}
                                            className="card-img-top"
                                            style={{
                                              height: '320px',
                                              objectFit: 'cover',
                                              transition: 'transform 0.3s ease',
                                            }}
                                            onMouseEnter={(e) => e.target.style.transform = 'scale(1.05)'}
                                            onMouseLeave={(e) => e.target.style.transform = 'scale(1)'}
                                          />
                                          {/* Status Badge Overlay */}
                                          <div
                                            style={{
                                              position: 'absolute',
                                              top: '10px',
                                              right: '10px',
                                            }}
                                          >
                                            <span
                                              className={`badge ${item.status ? 'bg-success' : 'bg-secondary'}`}
                                              style={{
                                                fontSize: '0.75rem',
                                                padding: '6px 12px',
                                                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                                              }}
                                            >
                                              <i className={`fa fa-${item.status ? 'eye' : 'eye-slash'} me-1`}></i>
                                              {item.status ? 'Visible' : 'Hidden'}
                                            </span>
                                          </div>
                                        </div>
                                      )}

                                      {/* Card Body */}
                                      <div className="card-body d-flex flex-column">
                                        <div className="mb-3 flex-grow-1">
                                          <h5 className="card-title mb-1 fw-bold">{item.name}</h5>
                                          <p className="card-text text-primary small mb-2">
                                            <i className="fa fa-briefcase me-1"></i>
                                            {item.title}
                                          </p>
                                          {item.bio && (
                                            <p
                                              className="card-text text-muted small mb-0"
                                              style={{
                                                display: '-webkit-box',
                                                WebkitLineClamp: 3,
                                                WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden',
                                                lineHeight: '1.5',
                                              }}
                                            >
                                              {item.bio}
                                            </p>
                                          )}
                                        </div>

                                        {/* Actions */}
                                        <div className="border-top pt-3 mt-auto">
                                          <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                            <div className="d-flex align-items-center">
                                              <span className="text-muted small me-2">Status:</span>
                                              <ReactSwitch
                                                uncheckedIcon={false}
                                                checkedIcon={false}
                                                onChange={() => handleToggleStatus(item._id, item.status)}
                                                checked={item.status}
                                                height={22}
                                                width={44}
                                                onColor="#28a745"
                                                offColor="#6c757d"
                                              />
                                            </div>
                                            <div className="d-flex gap-2">
                                              <button
                                                className="btn btn-sm btn-outline-primary px-3"
                                                onClick={() => handleEdit(item)}
                                                title="Edit Expert"
                                              >
                                                <img src={editIcon} alt="Edit" width="16" height="16" />
                                              </button>
                                              <button
                                                className="btn btn-sm btn-outline-danger px-3"
                                                onClick={() => handleDelete(item._id)}
                                                title="Delete Expert"
                                              >
                                                <img src={deleteIcon} alt="Delete" width="16" height="16" />
                                              </button>
                                            </div>
                                          </div>
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                )}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </DragDropContext>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Add/Edit Modal */}
      <CustomModal
        title={
          <div className="d-flex align-items-center">
            <i className={`fa fa-${editingItem ? 'edit' : 'plus-circle'} me-2 text-primary`}></i>
            {editingItem ? 'Edit Expert' : 'Add New Expert'}
          </div>
        }
        show={showModal}
        handleToggle={() => setShowModal(false)}
        size="lg"
      >
        <Formik
          initialValues={{
            name: editingItem?.name || '',
            title: editingItem?.title || '',
            bio: editingItem?.bio || '',
            image: editingItem?.image || [],
            status: editingItem ? editingItem.status : true,
          }}
          validationSchema={validationSchema}
          onSubmit={onSubmit}
          enableReinitialize
        >
          {(formik) => (
            <Form className="p-3">
              <div className="alert alert-light border mb-4">
                <small className="text-muted">
                  <i className="fa fa-info-circle me-2"></i>
                  Add healthcare professionals to showcase your team&apos;s expertise
                </small>
              </div>

              <CustomInput
                name="name"
                label="Full Name"
                placeholder="Dr. John Doe"
                labelclassname="form-label fw-semibold"
                className="form-control mb-3"
                isRequired
              />

              <CustomInput
                name="title"
                label="Professional Title"
                placeholder="Senior Pharmacist, Clinical Director"
                labelclassname="form-label fw-semibold"
                className="form-control mb-3"
                isRequired
              />

              <CustomInput
                name="bio"
                label="Biography"
                placeholder="Brief professional background and expertise..."
                labelclassname="form-label fw-semibold"
                className="form-control mb-3"
                as="textarea"
                rows={4}
              />
              <small className="text-muted d-block mb-3">
                Optional: Provide a brief overview of qualifications and specializations
              </small>

              <ImageInput
                label="Professional Photo"
                name="image"
                id="expert_image"
                accept="image/*"
                imagesLimit={1}
                isRequired
              />
              <small className="text-muted d-block mb-3">
                Upload a professional photo of the expert
              </small>

              <div className="mb-4 p-3 bg-light rounded">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <label className="form-label fw-semibold mb-1">Visibility Status</label>
                    <p className="text-muted small mb-0">
                      {formik.values.status ? 'This expert will be visible on the website' : 'This expert will be hidden from the website'}
                    </p>
                  </div>
                  <ReactSwitch
                    uncheckedIcon={false}
                    checkedIcon={false}
                    onChange={(checked) => formik.setFieldValue('status', checked)}
                    checked={formik.values.status}
                    height={26}
                    width={52}
                    onColor="#28a745"
                    offColor="#6c757d"
                  />
                </div>
              </div>

              <div className="footer_main mt-4 pt-3 border-top d-flex justify-content-end gap-2">
                <Button
                  type="button"
                  variant="outline-secondary"
                  onClick={() => {
                    setShowModal(false);
                    setEditingItem(null);
                  }}
                >
                  <i className="fa fa-times me-2"></i>
                  Cancel
                </Button>
                <Button type="submit" variant="primary" disabled={loading}>
                  <i className={`fa fa-${loading ? 'spinner fa-spin' : editingItem ? 'save' : 'plus'} me-2`}></i>
                  {loading ? 'Saving...' : editingItem ? 'Update Expert' : 'Add Expert'}
                </Button>
              </div>
            </Form>
          )}
        </Formik>
      </CustomModal>

      {/* Delete Modal */}
      <DeleteConfirmationModal
        name="Expert"
        show={showDeleteModal}
        handleToggle={() => setShowDeleteModal(false)}
        handleDelete={confirmDelete}
      />

      {/* Theme Editor */}
      <ThemeEditor
        show={showThemeEditor}
        onHide={() => setShowThemeEditor(false)}
        sectionKey={section.sectionKey}
        sectionName="Experts"
      />
    </div>
  );
};

export default ExpertsManager;
